import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  addBonusRound,
  addRound,
  addSegment,
  deleteBonusRound,
  deleteRound,
  deleteSegment,
  moveRound,
  renameBonusRound,
  renameRound,
  setSegmentWeight,
  unlockSegmentWeight,
  updateSegment,
  type WheelRef,
} from '../../budget/repo'
import { publishGame } from '../../lib/api'
import { useStore } from '../../store'
import { Card, CommittedInput, Dialog, Sheet, TopBar } from '../../ui/parts'
import {
  MAX_BONUS_ROUNDS,
  MAX_ROUNDS,
  MAX_SEGMENTS,
  MIN_ROUNDS,
  MIN_SEGMENTS,
  SPIN_DURATION_MS,
  TAU,
  buildSteps,
  easeOutCubic,
  insertBonus,
  parsePercent,
  sliceRanges,
  spinTarget,
  winningSlice,
  type GameConfig,
  type RoundResult,
  type Segment,
  type Step,
} from '../game'

/** Same speed bump as the budget admin panel: enough to stop idle fiddling. */
const CONFIG_PASSCODE = '4321'

type Phase = 'ready' | 'spinning' | 'landed' | 'finished'

export function SpinnerScreen() {
  const { snapshot } = useStore()
  const config: GameConfig | null = snapshot?.game ?? null

  // The game in progress is deliberately not persisted — a finished game is meant to be
  // forgotten, and a half-played one is not worth syncing between devices.
  const [steps, setSteps] = useState<Step[]>(() => (config ? buildSteps(config) : []))
  const [results, setResults] = useState<RoundResult[]>([])
  const [pending, setPending] = useState<RoundResult | null>(null)
  const [phase, setPhase] = useState<Phase>('ready')
  const [configOpen, setConfigOpen] = useState(false)

  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const rotationRef = useRef(0)
  const frameRef = useRef<number | null>(null)

  const index = results.length
  const step: Step | null = steps[index] ?? null
  const segments = useMemo(() => step?.segments ?? [], [step])

  // Changing the configuration mid-game would leave the played rounds pointing at wheels
  // that no longer exist, so a config change starts the game over.
  const configKey = JSON.stringify(config)
  useEffect(() => {
    if (!config) return
    setSteps(buildSteps(config))
    setResults([])
    setPending(null)
    setPhase('ready')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configKey])

  const draw = useCallback(
    (rotation: number) => {
      const canvas = canvasRef.current
      if (!canvas || segments.length === 0) return
      const ctx = canvas.getContext('2d')
      if (!ctx) return

      const size = canvas.clientWidth
      if (size === 0) return
      const dpr = window.devicePixelRatio || 1
      if (canvas.width !== Math.round(size * dpr)) {
        canvas.width = Math.round(size * dpr)
        canvas.height = Math.round(size * dpr)
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      ctx.clearRect(0, 0, size, size)

      const centre = size / 2
      const radius = centre - 6
      const ranges = sliceRanges(segments)

      ctx.save()
      ctx.translate(centre, centre)
      ctx.rotate(rotation)

      segments.forEach((segment, i) => {
        const { start, end } = ranges[i]
        ctx.beginPath()
        ctx.moveTo(0, 0)
        ctx.arc(0, 0, radius, start, end)
        ctx.closePath()
        ctx.fillStyle = segment.colour
        ctx.fill()

        ctx.beginPath()
        ctx.moveTo(0, 0)
        ctx.lineTo(Math.cos(end) * radius, Math.sin(end) * radius)
        ctx.strokeStyle = 'rgba(255,255,255,0.15)'
        ctx.lineWidth = 1.5
        ctx.stroke()
      })

      ctx.beginPath()
      ctx.arc(0, 0, radius * 0.12, 0, TAU)
      ctx.fillStyle = '#1a1a2e'
      ctx.fill()
      ctx.strokeStyle = 'rgba(255,255,255,0.1)'
      ctx.lineWidth = 1.5
      ctx.stroke()

      ctx.beginPath()
      ctx.arc(0, 0, radius, 0, TAU)
      ctx.strokeStyle = 'rgba(255,255,255,0.12)'
      ctx.lineWidth = 2
      ctx.stroke()

      // Labels are sized and truncated against the slice they sit in, so a 5% sliver does
      // not carry text across its neighbours.
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      segments.forEach((segment, i) => {
        const { start, end } = ranges[i]
        const mid = (start + end) / 2
        const share = (end - start) / TAU
        const fontSize = Math.max(9, Math.min(radius * 0.12, radius * share * 1.5))
        const maxChars = Math.max(3, Math.round(share * 62))

        ctx.save()
        ctx.translate(Math.cos(mid) * radius * 0.62, Math.sin(mid) * radius * 0.62)
        ctx.rotate(mid + Math.PI / 2)
        ctx.font = `600 ${fontSize}px Inter, system-ui, sans-serif`
        ctx.fillStyle = '#ffffff'
        const label =
          segment.label.length > maxChars
            ? `${segment.label.slice(0, Math.max(1, maxChars - 1))}…`
            : segment.label
        ctx.fillText(label, 0, 0)
        ctx.restore()
      })

      ctx.restore()
    },
    [segments]
  )

  useEffect(() => {
    draw(rotationRef.current)
    const onResize = () => draw(rotationRef.current)
    window.addEventListener('resize', onResize)
    return () => {
      window.removeEventListener('resize', onResize)
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current)
    }
  }, [draw])

  function spin() {
    if (phase !== 'ready' || !step || segments.length < MIN_SEGMENTS) return
    setPhase('spinning')
    setPending(null)

    const from = rotationRef.current
    const to = spinTarget(from)
    const start = performance.now()

    const frame = (now: number) => {
      const progress = Math.min((now - start) / SPIN_DURATION_MS, 1)
      rotationRef.current = from + (to - from) * easeOutCubic(progress)
      draw(rotationRef.current)

      if (progress < 1) {
        frameRef.current = requestAnimationFrame(frame)
        return
      }

      rotationRef.current = to
      draw(to)
      const winner = segments[winningSlice(to, segments)]
      setPending({
        title: step.title,
        label: winner.label,
        colour: winner.colour,
        bonus: step.bonus,
        triggered: winner.bonusRoundId,
      })
      setPhase('landed')
      navigator.vibrate?.(30)
    }

    frameRef.current = requestAnimationFrame(frame)
  }

  /** Accepts the spin. A trigger splices its bonus round in as the very next step. */
  function accept() {
    if (!pending || !config) return
    const nextResults = [...results, pending]
    let nextSteps = steps
    if (pending.triggered) {
      nextSteps = insertBonus(steps, index, config, pending.triggered, String(index))
    }
    setResults(nextResults)
    setSteps(nextSteps)
    setPending(null)
    setPhase(nextResults.length >= nextSteps.length ? 'finished' : 'ready')
  }

  function newGame() {
    if (!config) return
    setSteps(buildSteps(config))
    setResults([])
    setPending(null)
    setPhase('ready')
  }

  if (!snapshot || !config) return null

  return (
    <div className="screen">
      <TopBar
        title="Spinner"
        subtitle={phase === 'finished' ? 'Game complete' : `Round ${index + 1} of ${steps.length}`}
        actions={
          <button
            type="button"
            className="icon-btn"
            onClick={() => setConfigOpen(true)}
            aria-label="Configuration"
          >
            ⚙
          </button>
        }
      />

      {phase === 'finished' ? (
        <div className="stack">
          <Card>
            <div className="card-title">Everything you landed on</div>
            <div style={{ marginTop: 8 }}>
              {results.map((result, i) => (
                <div className="round-line" key={i}>
                  <div className="round-line-title">{result.title}</div>
                  <div className="round-line-winner" style={{ color: result.colour }}>
                    {result.label}
                  </div>
                </div>
              ))}
            </div>
            <button type="button" className="btn block" style={{ marginTop: 14 }} onClick={newGame}>
              Start a new game
            </button>
          </Card>
        </div>
      ) : (
        <>
          {/* The round title is the one thing you look up at between spins, so it gets the
              width and the weight rather than sitting in the bar. */}
          <div className="round-heading">
            <div className="round-heading-title">{step?.title || `Round ${index + 1}`}</div>
            <div className="round-heading-sub">
              {step?.bonus ? 'Bonus round · ' : ''}
              {index + 1} of {steps.length}
            </div>
          </div>

          <div className="wheel-stage">
            <div className="wheel-pointer" aria-hidden />
            <canvas ref={canvasRef} className="wheel-canvas" />
          </div>

          <div className="spin-controls">
            {phase === 'landed' && pending ? (
              <>
                <div
                  className="winner"
                  style={{
                    background: `linear-gradient(135deg, ${pending.colour}22 0%, ${pending.colour}11 100%)`,
                    border: `1px solid ${pending.colour}44`,
                  }}
                >
                  <div className="lbl">{pending.title}</div>
                  <div style={{ fontSize: 24, fontWeight: 700, color: pending.colour }}>
                    {pending.label}
                  </div>
                  {pending.triggered ? (
                    <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                      Unlocks{' '}
                      {config.bonusRounds.find((b) => b.id === pending.triggered)?.title ??
                        'a bonus round'}
                    </div>
                  ) : null}
                </div>
                <button type="button" className="btn block" onClick={accept}>
                  {index + 1 >= steps.length && !pending.triggered ? 'Finish' : 'Next round'}
                </button>
              </>
            ) : (
              <button
                type="button"
                className="spin-btn"
                onClick={spin}
                disabled={phase === 'spinning' || segments.length < MIN_SEGMENTS}
              >
                {phase === 'spinning' ? 'SPINNING…' : 'SPIN WHEEL'}
              </button>
            )}
          </div>

          {/* The running tally sits under the wheel so the wheel itself gets the width. */}
          <Card style={{ marginTop: 14 }}>
            <div className="lbl" style={{ marginBottom: 6 }}>
              Rounds
            </div>
            {steps.map((s, i) => {
              const result = results[i]
              const current = i === index
              return (
                <div className={`round-line ${current ? 'current' : ''}`} key={s.key}>
                  <div className="round-line-title">
                    {s.title}
                    {s.bonus ? ' ★' : ''}
                  </div>
                  <div
                    className="round-line-winner"
                    style={{ color: result ? result.colour : 'var(--ink-muted)' }}
                  >
                    {result ? result.label : current ? 'up next' : '—'}
                  </div>
                </div>
              )
            })}
            {results.length > 0 && phase !== 'spinning' ? (
              <button
                type="button"
                className="btn ghost small"
                style={{ paddingLeft: 0, marginTop: 4 }}
                onClick={newGame}
              >
                Start a new game
              </button>
            ) : null}
          </Card>
        </>
      )}

      {configOpen ? <ConfigSheet onClose={() => setConfigOpen(false)} /> : null}
    </div>
  )
}

function ConfigSheet({ onClose }: { onClose: () => void }) {
  const { snapshot, mutate, showToast } = useStore()
  const [unlocked, setUnlocked] = useState(false)
  const [passcode, setPasscode] = useState('')
  const [error, setError] = useState(false)
  const [confirmPublish, setConfirmPublish] = useState(false)
  const [publishing, setPublishing] = useState(false)
  // Collapsed by default: with a wheel per round the full list runs to several screens,
  // and you are normally here to change one of them.
  const [openRounds, setOpenRounds] = useState<Record<string, boolean>>({})
  const [openBonus, setOpenBonus] = useState<Record<string, boolean>>({})

  if (!snapshot) return null
  const { rounds, bonusRounds } = snapshot.game

  if (!unlocked) {
    return (
      <Sheet onClose={onClose}>
        <h2 style={{ margin: '0 0 4px', fontSize: 20 }}>Configuration</h2>
        <div className="muted" style={{ fontSize: 13, marginBottom: 14 }}>
          Enter the passcode to change the wheel.
        </div>
        <div className="row">
          <input
            type="password"
            inputMode="numeric"
            autoFocus
            placeholder="Passcode"
            value={passcode}
            className={error ? 'error' : ''}
            onChange={(e) => {
              setPasscode(e.target.value.replace(/\D/g, '').slice(0, 8))
              setError(false)
            }}
            style={{ flex: 1 }}
          />
          <button
            type="button"
            className="btn"
            disabled={passcode === ''}
            onClick={() => {
              if (passcode === CONFIG_PASSCODE) setUnlocked(true)
              else {
                setError(true)
                setPasscode('')
              }
            }}
          >
            Unlock
          </button>
        </div>
        {error ? (
          <div className="over" style={{ fontSize: 13, marginTop: 8 }}>
            Wrong passcode
          </div>
        ) : null}
      </Sheet>
    )
  }

  return (
    <Sheet onClose={onClose}>
      <h2 style={{ margin: '0 0 14px', fontSize: 20 }}>Configuration</h2>

      <div className="card-title">Rounds</div>
      <div className="muted" style={{ fontSize: 12, margin: '2px 0 10px' }}>
        Each round has its own options. Percentages size the slices and always total 100.
        A percentage you type is locked and the others adjust around it — tap the padlock to
        release it.
      </div>

      {rounds.map((round, i) => {
        const open = openRounds[round.id] ?? false
        return (
          <div className="round-block" key={round.id}>
            <div className="row" style={{ gap: 6 }}>
              {/* − and + move the round up and down the running order: the number beside
                  them is what changes. */}
              <button
                type="button"
                className="icon-btn"
                aria-label={`Move ${round.title || `round ${i + 1}`} earlier`}
                disabled={i === 0}
                onClick={() => void mutate((s) => moveRound(s, round.id, -1))}
              >
                −
              </button>
              <span className="muted tnum" style={{ width: 16, textAlign: 'center', fontSize: 13 }}>
                {i + 1}
              </span>
              <button
                type="button"
                className="icon-btn"
                aria-label={`Move ${round.title || `round ${i + 1}`} later`}
                disabled={i === rounds.length - 1}
                onClick={() => void mutate((s) => moveRound(s, round.id, 1))}
              >
                +
              </button>

              <button
                type="button"
                className="round-toggle grow"
                aria-expanded={open}
                onClick={() => setOpenRounds((r) => ({ ...r, [round.id]: !open }))}
              >
                <span className="round-toggle-title">{round.title || `Round ${i + 1}`}</span>
                <span className="muted" style={{ fontSize: 12 }}>
                  {round.segments.length} option{round.segments.length === 1 ? '' : 's'}
                </span>
                <span className={`caret ${open ? 'open' : ''}`} aria-hidden>
                  ›
                </span>
              </button>

              <button
                type="button"
                className="icon-btn"
                aria-label="Remove round"
                disabled={rounds.length <= MIN_ROUNDS}
                onClick={() => void mutate((s) => deleteRound(s, round.id))}
              >
                ✕
              </button>
            </div>

            {open ? (
              <div style={{ marginTop: 10 }}>
                <CommittedInput
                  value={round.title}
                  placeholder={`Round ${i + 1}`}
                  onCommit={(title) => void mutate((s) => renameRound(s, round.id, title))}
                  style={{ width: '100%', fontWeight: 600, marginBottom: 8 }}
                />

                {round.segments.map((segment) => (
                  <SegmentRow
                    key={segment.id}
                    wheel={{ kind: 'round', id: round.id }}
                    segment={segment}
                    bonusRounds={bonusRounds}
                    canDelete={round.segments.length > MIN_SEGMENTS}
                  />
                ))}

                <button
                  type="button"
                  className="btn outline small"
                  style={{ marginTop: 8 }}
                  disabled={round.segments.length >= MAX_SEGMENTS}
                  onClick={() => void mutate((s) => addSegment(s, { kind: 'round', id: round.id }))}
                >
                  Add option
                </button>
              </div>
            ) : null}
          </div>
        )
      })}

      <button
        type="button"
        className="btn outline small"
        disabled={rounds.length >= MAX_ROUNDS}
        onClick={() => void mutate((s) => addRound(s))}
      >
        Add round
      </button>
      <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
        A new round starts as a copy of the last one.
      </div>

      <div className="card-title" style={{ margin: '22px 0 4px' }}>
        Bonus rounds
      </div>
      <div className="muted" style={{ fontSize: 12, marginBottom: 10 }}>
        Each has its own options. A bonus round is only played when an option points at it,
        so it is skipped whenever that option is not landed on.
      </div>

      {bonusRounds.map((bonus) => {
        const open = openBonus[bonus.id] ?? false
        return (
          <div className="bonus-block" key={bonus.id}>
            <div className="row" style={{ gap: 6 }}>
              <button
                type="button"
                className="round-toggle grow"
                aria-expanded={open}
                onClick={() => setOpenBonus((r) => ({ ...r, [bonus.id]: !open }))}
              >
                <span className="round-toggle-title">{bonus.title || 'Bonus round'}</span>
                <span className="muted" style={{ fontSize: 12 }}>
                  {bonus.segments.length} option{bonus.segments.length === 1 ? '' : 's'}
                </span>
                <span className={`caret ${open ? 'open' : ''}`} aria-hidden>
                  ›
                </span>
              </button>
              <button
                type="button"
                className="icon-btn"
                aria-label="Remove bonus round"
                onClick={() => void mutate((s) => deleteBonusRound(s, bonus.id))}
              >
                ✕
              </button>
            </div>

            {open ? (
              <div style={{ marginTop: 10 }}>
                <CommittedInput
                  value={bonus.title}
                  placeholder="Bonus round"
                  onCommit={(title) => void mutate((s) => renameBonusRound(s, bonus.id, title))}
                  style={{ width: '100%', fontWeight: 600, marginBottom: 8 }}
                />

                {bonus.segments.map((segment) => (
                  <SegmentRow
                    key={segment.id}
                    wheel={{ kind: 'bonus', id: bonus.id }}
                    segment={segment}
                    bonusRounds={[]}
                    canDelete={bonus.segments.length > MIN_SEGMENTS}
                  />
                ))}

                <button
                  type="button"
                  className="btn outline small"
                  style={{ marginTop: 8 }}
                  disabled={bonus.segments.length >= MAX_SEGMENTS}
                  onClick={() => void mutate((s) => addSegment(s, { kind: 'bonus', id: bonus.id }))}
                >
                  Add option
                </button>
              </div>
            ) : null}
          </div>
        )
      })}

      <button
        type="button"
        className="btn outline small"
        disabled={bonusRounds.length >= MAX_BONUS_ROUNDS}
        onClick={() => void mutate((s) => addBonusRound(s))}
      >
        Add bonus round
      </button>

      <div style={{ borderTop: '1px solid var(--hairline)', margin: '22px 0 0', paddingTop: 16 }}>
        <div className="card-title">Share this wheel</div>
        <div className="muted" style={{ fontSize: 12, margin: '2px 0 10px' }}>
          Copies these options, rounds and bonus rounds to the other account. Nothing else
          of theirs is touched, and they will see it next time they open the app.
        </div>
        <button
          type="button"
          className="btn outline"
          disabled={publishing}
          onClick={() => setConfirmPublish(true)}
        >
          {publishing ? 'Sending…' : 'Push to all users'}
        </button>
      </div>

      <button type="button" className="btn block" style={{ marginTop: 22 }} onClick={onClose}>
        Done
      </button>

      {confirmPublish ? (
        <Dialog
          title="Push this wheel to everyone?"
          body="The other account's spinner configuration will be replaced by this one. Their budget, food and other data are not affected."
          confirmLabel="Push it"
          onCancel={() => setConfirmPublish(false)}
          onConfirm={async () => {
            setConfirmPublish(false)
            setPublishing(true)
            try {
              const updated = await publishGame(snapshot.game)
              showToast(
                updated === 0
                  ? 'No other accounts to send it to'
                  : `Sent to ${updated} other account${updated === 1 ? '' : 's'}`
              )
            } catch {
              showToast('Could not send it — check your connection')
            } finally {
              setPublishing(false)
            }
          }}
        />
      ) : null}
    </Sheet>
  )
}

function SegmentRow({
  wheel,
  segment,
  bonusRounds,
  canDelete,
}: {
  wheel: WheelRef
  segment: Segment
  bonusRounds: { id: string; title: string }[]
  canDelete: boolean
}) {
  const { mutate } = useStore()
  const [percent, setPercent] = useState(String(segment.weight))

  // The field is free text, so it is only committed when you leave it — otherwise typing
  // "50" would rebalance at "5" first and fight you for the second keystroke.
  useEffect(() => setPercent(String(segment.weight)), [segment.weight])

  const commit = () => {
    const value = parsePercent(percent)
    if (value === null) setPercent(String(segment.weight))
    else if (value !== segment.weight) void mutate((s) => setSegmentWeight(s, wheel, segment.id, value))
  }

  return (
    <div className="config-row">
      <div className="row" style={{ gap: 8 }}>
        <span
          aria-hidden
          style={{ width: 14, height: 14, borderRadius: 4, background: segment.colour, flex: 'none' }}
        />
        <CommittedInput
          value={segment.label}
          onCommit={(label) => void mutate((s) => updateSegment(s, wheel, segment.id, { label }))}
          style={{ flex: 1 }}
        />
        <input
          type="text"
          inputMode="numeric"
          aria-label="Percentage"
          value={percent}
          onChange={(e) => setPercent(e.target.value.replace(/\D/g, '').slice(0, 3))}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
          }}
          style={{ width: 58, textAlign: 'right' }}
        />
        <span className="muted" style={{ fontSize: 13 }}>
          %
        </span>
        {/* A percentage you typed stays put: the others absorb the difference. Unlock it to
            let it move again. */}
        <button
          type="button"
          className={`icon-btn ${segment.locked ? 'locked' : ''}`}
          aria-label={segment.locked ? `Unlock ${segment.label}` : 'Not locked'}
          title={segment.locked ? 'Locked — tap to let it adjust again' : 'Adjusts automatically'}
          disabled={!segment.locked}
          onClick={() => void mutate((s) => unlockSegmentWeight(s, wheel, segment.id))}
        >
          {segment.locked ? '🔒' : '·'}
        </button>
        <button
          type="button"
          className="icon-btn"
          aria-label={`Remove ${segment.label}`}
          disabled={!canDelete}
          onClick={() => void mutate((s) => deleteSegment(s, wheel, segment.id))}
        >
          ✕
        </button>
      </div>

      {bonusRounds.length > 0 ? (
        <div style={{ marginTop: 8 }}>
          <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>
            Landing here then plays
          </div>
          {/* Chips rather than a <select>: a native dropdown inside a bottom sheet is
              awkward to tap on a phone, and its option list renders outside the sheet. */}
          <div className="trigger-chips">
            <button
              type="button"
              className={`trigger-chip ${segment.bonusRoundId === null ? 'on' : ''}`}
              onClick={() =>
                void mutate((s) => updateSegment(s, wheel, segment.id, { bonusRoundId: null }))
              }
            >
              Nothing extra
            </button>
            {bonusRounds.map((bonus) => (
              <button
                key={bonus.id}
                type="button"
                className={`trigger-chip ${segment.bonusRoundId === bonus.id ? 'on' : ''}`}
                onClick={() =>
                  void mutate((s) =>
                    updateSegment(s, wheel, segment.id, { bonusRoundId: bonus.id })
                  )
                }
              >
                {bonus.title.trim() || 'Bonus round'}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  )
}
